/**
 * InstitutionalInvoiceTable (Plan 14).
 *
 * Paginated invoice rows. Adds a School column when the viewer admins
 * >1 schools (visible whenever the parent decides the org-level view
 * is active). Per the mockup feedback note in the plan:
 *
 *   - rows are left-aligned (currency / amounts right-aligned)
 *   - the dispute / standalone ACH / standalone CSV buttons are gone
 *   - each row exposes a "…" overflow menu (Download Summary PDF +
 *     Export Orders CSV)
 *
 * Clicking the row body opens the detail drawer; the overflow menu
 * stops propagation so the row click doesn't open the drawer when
 * the user is targeting a menu item.
 */

import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@ordo/ui/dropdown-menu'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from '@ordo/ui/table'
import { MoreHorizontal } from 'lucide-react'

import type { SchoolARInvoiceRow } from '@/api/billing-school'
import { cn } from '@/lib/utils'

import {
  bucketLabel,
  formatCents,
  formatDate,
  invoiceStatusLabel,
  invoiceStatusTone,
  outstandingCents,
  paymentStateDetail,
  paymentStateLabel,
  paymentStateTone,
} from './school-ar-helpers'

export interface InstitutionalInvoiceTableProps {
  rows: readonly SchoolARInvoiceRow[]
  /** True when the org-level view is rendered (multi-school admin). */
  showSchoolColumn: boolean
  onRowClick: (row: SchoolARInvoiceRow) => void
  onDownloadSummaryPdf: (row: SchoolARInvoiceRow) => void
  onExportOrdersCsv: (row: SchoolARInvoiceRow) => void
}

export function InstitutionalInvoiceTable({
  rows,
  showSchoolColumn,
  onRowClick,
  onDownloadSummaryPdf,
  onExportOrdersCsv,
}: InstitutionalInvoiceTableProps) {
  if (rows.length === 0) {
    return <EmptyState />
  }
  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Payment</TableHead>
            <TableHead>Period</TableHead>
            <TableHead>Due</TableHead>
            <TableHead>Bucket</TableHead>
            {showSchoolColumn ? <TableHead>School</TableHead> : null}
            <TableHead className="text-right">Total</TableHead>
            <TableHead className="text-right">Paid</TableHead>
            <TableHead className="text-right">Outstanding</TableHead>
            <TableHead className="w-[40px]" aria-label="Row actions" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <InvoiceRow
              key={r.id}
              row={r}
              showSchoolColumn={showSchoolColumn}
              onRowClick={() => onRowClick(r)}
              onDownloadSummaryPdf={() => onDownloadSummaryPdf(r)}
              onExportOrdersCsv={() => onExportOrdersCsv(r)}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

interface InvoiceRowProps {
  row: SchoolARInvoiceRow
  showSchoolColumn: boolean
  onRowClick: () => void
  onDownloadSummaryPdf: () => void
  onExportOrdersCsv: () => void
}

function InvoiceRow({ row, showSchoolColumn, onRowClick, onDownloadSummaryPdf, onExportOrdersCsv }: InvoiceRowProps) {
  const outstanding = outstandingCents(row)
  const isPastDue = row.bucket !== 'current' && outstanding > 0
  const paymentDetail = paymentStateDetail(row)
  // A Campus Admin's district invoice is shown, never opened: its detail and
  // downloads cover campuses they don't administer (PROD-1467).
  const actionable = !row.districtInvoiceForCampusAdmin
  return (
    <TableRow
      className={cn(actionable && 'cursor-pointer hover:bg-muted/40', isPastDue && 'bg-destructive/5')}
      onClick={(e) => {
        if (!actionable) {
          return
        }
        // The dropdown menu lives inside the row; clicks on the menu
        // trigger / its items must not bubble up to open the drawer.
        const target = e.target as HTMLElement
        if (target.closest('[data-row-action="true"]')) {
          return
        }
        onRowClick()
      }}>
      <TableCell>
        <Badge variant={invoiceStatusTone(row.status)}>{invoiceStatusLabel(row.status)}</Badge>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-0.5">
          <Badge variant={paymentStateTone(row.paymentState)}>{paymentStateLabel(row.paymentState)}</Badge>
          {paymentDetail ? (
            <span className="text-xs whitespace-nowrap text-muted-foreground">{paymentDetail}</span>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap text-sm">
        {formatDate(row.periodStart)} – {formatDate(row.periodEnd)}
      </TableCell>
      <TableCell className="whitespace-nowrap text-sm">{formatDate(row.dueAt)}</TableCell>
      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
        {/* A row the aging cards do not count has no bucket to show. */}
        {outstanding > 0 ? bucketLabel(row.bucket) : '—'}
      </TableCell>
      {showSchoolColumn ? (
        <TableCell className="whitespace-nowrap text-sm">{row.schoolLocationName ?? row.organizationName}</TableCell>
      ) : null}
      <TableCell className="text-right font-medium tabular-nums">{formatCents(row.totalCents, row.currency)}</TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">
        {formatCents(row.paidCents, row.currency)}
      </TableCell>
      <TableCell className={cn('text-right tabular-nums font-medium', isPastDue && 'text-destructive')}>
        {formatCents(outstanding, row.currency)}
      </TableCell>
      <TableCell data-row-action="true" onClick={(e) => e.stopPropagation()}>
        {actionable ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Row actions">
                <MoreHorizontal className="h-4 w-4" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onDownloadSummaryPdf}>Download invoice summary (PDF)</DropdownMenuItem>
              <DropdownMenuItem onClick={onExportOrdersCsv}>Download item manifest (CSV)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </TableCell>
    </TableRow>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-12 text-center text-sm">
      <p className="font-medium text-foreground">No invoices in view</p>
      <p className="max-w-sm text-xs text-muted-foreground">
        Try clearing the school or aging filter to see the full ledger.
      </p>
    </div>
  )
}
